import { rerenderSection } from "./utils.js";

if (!customElements.get("pagination-nav")) {
  customElements.define(
    "pagination-nav",
    class Pagination extends HTMLElement {
      constructor() {
        super();

        this.paginationNavButtons = this.querySelectorAll(
          "[data-pagination-nav-button]"
        );

        this.paginationNavTargetId = this.dataset.paginationNavTargetId;

        /*
         * This sectionId is retrieved from the dataset attribute of the pagination-nav element.
         * It is set in the pagination-nav.liquid snippet using Liquid templating,
         * specifically with data-section-id="{{ section.id }}".
         *
         * The sectionId is crucial for identifying the specific Shopify section to update
         * during pagination. It allows us to construct the correct DOM ID for rerendering
         * only that section's content, enabling partial page updates for better performance
         * and user experience in collection pages.
         *
         * We need this dynamic sectionId to make the pagination-nav custom element reusable
         * across different section liquid files. By dynamically retrieving the section.id
         * in any section liquid, we can rerender the entire corresponding section without
         * hardcoding IDs, promoting modularity and flexibility in theme development.
         */
        this.sectionId = this.dataset.sectionId;
      }

      connectedCallback() {
        this.setInitialActivePage();
        this.paginationNavButtons.forEach((button) => {
          button.addEventListener(
            "click",
            this.handlePaginationNavButtons.bind(this)
          );

          button.setAttribute("role", "button");
          button.setAttribute("tabindex", "0");
        });
      }

      disconnectedCallback() {
        this.paginationNavButtons.forEach((button) => {
          button.removeEventListener("click", this.handlePaginationNavButtons);
        });
      }

      setInitialActivePage() {
        const currentUrl = new URL(window.location.href);
        const currentPage = currentUrl.searchParams.get("page") || "1";

        this.paginationNavButtons.forEach((button) => {
          let buttonPage;

          if (button.dataset.href) {
            buttonPage =
              new URL(
                button.dataset.href,
                window.location.origin
              ).searchParams.get("page") || "1";
          } else {
            buttonPage = button.textContent.trim();
          }

          if (buttonPage === currentPage) {
            button.classList.add("active");
            button.setAttribute("aria-current", "page");
          } else {
            button.classList.remove("active");
            button.removeAttribute("aria-current");
          }
        });
      }

      handlePaginationNavButtons(event) {
        event.preventDefault();

        // 1. Get the button that was clicked
        const button = event.currentTarget;

        // 2. Get the page url value from the button
        const href = button.dataset.href;

        // 3. Create URL
        const url = new URL(href, window.location.href);

        // 2. Get the page number from the button
        const pageNumber = url.searchParams.get("page");

        // 3. Remove the active class from all pagination buttons
        this.paginationNavButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.removeAttribute("aria-current");
        });

        // 4. Add the active class to the clicked button
        button.classList.add("active");
        button.setAttribute("aria-current", "page");

        // 5. Update the history state
        this.updateHistoryWithoutSectionId(pageNumber);

        // 6. Build the fetch URL with section_id parameter
        const fetchUrl = this.buildFetchUrlWithSectionId(pageNumber);

        // 7. Rerender the section using the rerenderSection function
        // We fetch the url from fetchUrl (e.g. https://shopify.com/collection/all?page=2&section_id=collection_id) and rerender the section using the rerenderSection function
        rerenderSection(fetchUrl, this.paginationNavTargetId, "elementId");
      }

      updateHistoryWithoutSectionId(pageNumber) {
        const currentUrl = new URL(window.location.href);

        this.updateSearchParams(currentUrl, pageNumber);

        // Use replaceState if you’d rather not clutter the history on first-page clicks:
        history.replaceState(null, "", currentUrl.toString());
      }

      buildFetchUrlWithSectionId(pageNumber) {
        if (!this.sectionId) {
          throw new Error("Missing required sectionId");
        }

        /**
         * URL CONSTRUCTION FOR SECTION RERENDERING:
         * Builds a fetch URL that includes both sorting parameters and section identification
         * for Shopify's section rendering system.
         *
         * PROCESS FLOW:
         * 1. Clone current page URL to preserve existing parameters and context
         * 2. Apply sort parameter updates while maintaining other query parameters
         * 3. Append section_id for targeted section rerendering capability
         * 4. Generate final fetch URL for AJAX section updates
         *
         * URL STRUCTURE EXAMPLE:
         * Input:  https://shop.com/collections/all?page=2
         * Output: https://shop.com/collections/all?page=2&sort_by=price-ascending&section_id=collection-123
         *
         * SHOPIFY INTEGRATION:
         * The section_id parameter enables Shopify's built-in section rendering endpoint,
         * allowing partial page updates without full page reloads for better performance.
         */
        const currentUrl = new URL(window.location.href);

        // Apply sorting parameters while preserving existing query state
        this.updateSearchParams(currentUrl, pageNumber);

        // Enable section-specific rendering for targeted DOM updates
        currentUrl.searchParams.set("section_id", this.sectionId);

        // Return complete fetch URL for section rerendering request
        return currentUrl.toString();
      }

      updateSearchParams(currentUrl, pageNumber) {
        const searchParams = currentUrl.searchParams;

        if (pageNumber === "1") {
          // Remove page parameter for page 1 (cleaner URLs)
          searchParams.delete("page");
        } else {
          searchParams.set("page", pageNumber);
        }
      }
    }
  );
}

if (!customElements.get("sort-by")) {
  customElements.define(
    "sort-by",
    class SortBy extends HTMLElement {
      constructor() {
        super();

        this.sortOption = this.querySelectorAll("[data-sort-option]");

        this.sortByTargetId = this.dataset.sortByTargetId;
        this.sectionId = this.dataset.sectionId;
      }

      connectedCallback() {
        this.setInitialActiveOption();
        this.sortOption.forEach((button) => {
          button.addEventListener("click", this.handleSort.bind(this));
          button.setAttribute("role", "button");
          button.setAttribute("tabindex", "0");
        });
      }

      disconnectedCallback() {
        this.sortOption.forEach((button) => {
          button.removeEventListener("click", this.handleSort);
        });
      }

      setInitialActiveOption() {
        const currentUrl = new URL(window.location.href);
        const currentSort = currentUrl.searchParams.get("sort_by") || "manual";
        this.sortOption.forEach((button) => {
          if (button.dataset.sortValue === currentSort) {
            button.classList.add("active");
            button.setAttribute("aria-selected", "true");
          } else {
            button.classList.remove("active");
            button.setAttribute("aria-selected", "false");
          }
        });
      }

      handleSort(event) {
        event.preventDefault();

        const button = event.currentTarget;

        const sortValue = button.dataset.sortValue;

        this.sortOption.forEach((btn) => {
          btn.classList.remove("active");
          btn.setAttribute("aria-selected", "false");
        });

        button.classList.add("active");
        button.setAttribute("aria-selected", "true");

        this.updateHistoryWithoutSectionId(sortValue);

        const fetchUrl = this.buildFetchUrlWithSectionId(sortValue);

        rerenderSection(fetchUrl, this.sortByTargetId, "elementId");
      }

      updateHistoryWithoutSectionId(sortValue) {
        const currentUrl = new URL(window.location.href);
        const searchParams = currentUrl.searchParams;

        searchParams.set("sort_by", sortValue);

        history.replaceState(null, "", currentUrl.toString());
      }

      buildFetchUrlWithSectionId(sortValue) {
        if (!this.sectionId) {
          throw new Error("Missing required sectionId");
        }

        const currentUrl = new URL(window.location.href);

        this.updateSearchParams(currentUrl, sortValue);

        currentUrl.searchParams.set("section_id", this.sectionId);

        return currentUrl.toString();
      }

      updateSearchParams(currentUrl, sortValue) {
        const searchParams = currentUrl.searchParams;
        searchParams.set("sort_by", sortValue);
      }
    }
  );
}

if (!customElements.get("dynamic-filters")) {
  customElements.define(
    "dynamic-filters",
    class ProductFilters extends HTMLElement {
      constructor() {
        super();

        this.filterTargetId = this.dataset.filterTargetId;
        this.sectionId = this.dataset.sectionId;

        this.filterInputs = this.querySelectorAll("input");
        this.handleInputChange = this.handleInputChange.bind(this);
      }

      connectedCallback() {
        this.filterInputs.forEach((input) => {
          input.addEventListener("change", this.handleInputChange);
        });
      }

      disconnectedCallback() {
        this.filterInputs.forEach((input) => {
          input.removeEventListener("change", this.handleInputChange);
        });
      }

      handleInputChange() {
        try {
          const activeInputs = this.getActiveInputs();
          const fetchUrl = this.buildFetchUrlWithSectionId(activeInputs);

          this.updateHistoryWithoutSectionId(activeInputs);

          rerenderSection(fetchUrl, this.filterTargetId, "elementId");
        } catch (error) {
          console.error("Error handling filter input change:", error);
        }
      }

      getActiveInputs() {
        return Array.from(this.filterInputs).filter((input) => {
          if (!input.name) {
            console.warn("Filter input missing name attribute:", input);
            return false;
          }
          if (input.type === "checkbox" || input.type === "radio") {
            return input.checked;
          } else {
            return input.value !== "";
          }
        });
      }

      updateHistoryWithoutSectionId(activeInputs) {
        try {
          const currentUrl = new URL(window.location.href);

          this.updateSearchParams(currentUrl, activeInputs);

          history.replaceState(null, "", currentUrl.toString());
        } catch (error) {
          console.error("Error updating browser history:", error);
        }
      }

      buildFetchUrlWithSectionId(activeInputs) {
        if (!this.sectionId) {
          throw new Error("Missing required sectionId");
        }

        const currentUrl = new URL(window.location.href);

        this.updateSearchParams(currentUrl, activeInputs);

        currentUrl.searchParams.set("section_id", this.sectionId);

        return currentUrl.toString();
      }

      updateSearchParams(currentUrl, activeInputs) {
        const searchParams = currentUrl.searchParams;

        this.clearFilterParameters(searchParams);

        activeInputs.forEach((input) => {
          if (input.name && input.value) {
            searchParams.append(input.name, input.value);
          }
        });

        searchParams.delete("page");
      }

      clearFilterParameters(searchParams) {
        const allFilterKeys = new Set(
          Array.from(this.filterInputs)
            .map((input) => input.name)
            .filter((param) => param)
        );

        allFilterKeys.forEach((key) => searchParams.delete(key));
      }
    }
  );
}
